import React, { useEffect, useMemo, useState } from "react";
import { App, Button, Card, Carousel, Input, Space, Switch, Upload, Typography } from "antd";
import { DeleteOutlined, EditOutlined, UploadOutlined, ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import {
  createAgentSiteSliderItem,
  deleteAgentSiteSliderItem,
  fetchAgentSiteSlider,
  reorderAgentSiteSlider,
  updateAgentSiteSliderItem,
  uploadAgentSiteSliderImage,
} from "../../../services/agentSiteSliderApi";

const { Text, Title } = Typography;
const { TextArea } = Input;

const EMPTY_FORM = {
  image_url: "",
  image_key: "",
  title: "",
  description: "",
  button_text: "",
  button_url: "",
  destination_code: "",
  destination_name: "",
  sort_order: 0,
  is_active: true,
};

export default function AgentSiteSliderSettings() {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);

  const activeItems = useMemo(
    () =>
      (items || [])
        .filter((x) => x?.is_active)
        .slice()
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [items]
  );

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await fetchAgentSiteSlider();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      message.error(e?.response?.data?.message || "טעינת סליידר נכשלה");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const onUpload = async (file) => {
    setUploading(true);
    try {
      const uploaded = await uploadAgentSiteSliderImage(file);
      setForm((prev) => ({
        ...prev,
        image_url: uploaded?.image_url || "",
        image_key: uploaded?.image_key || "",
      }));
      message.success("התמונה הועלתה בהצלחה");
    } catch (e) {
      message.error(e?.response?.data?.message || "העלאת תמונה נכשלה");
    } finally {
      setUploading(false);
    }
    return false;
  };

  const validate = () => {
    if (!String(form.image_url || "").trim()) {
      return "יש להעלות תמונה לפני שמירה";
    }
    return null;
  };

  const onSave = async () => {
    const err = validate();
    if (err) {
      message.error(err);
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const updated = await updateAgentSiteSliderItem(editingId, form);
        setItems((prev) => prev.map((x) => (x.id === editingId ? updated : x)));
        message.success("הסלייד עודכן");
      } else {
        const created = await createAgentSiteSliderItem(form);
        setItems((prev) => [...prev, created]);
        message.success("הסלייד נוסף");
      }
      resetForm();
      await loadItems();
    } catch (e) {
      message.error(e?.response?.data?.message || "שמירה נכשלה");
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (item) => {
    setEditingId(item.id);
    setForm({
      image_url: item.image_url || "",
      image_key: item.image_key || "",
      title: item.title || "",
      description: item.description || "",
      button_text: item.button_text || "",
      button_url: item.button_url || "",
      destination_code: item.destination_code || "",
      destination_name: item.destination_name || "",
      sort_order: Number(item.sort_order) || 0,
      is_active: item.is_active !== false,
    });
  };

  const onDelete = async (item) => {
    try {
      await deleteAgentSiteSliderItem(item.id);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      if (editingId === item.id) {
        resetForm();
      }
      message.success("התמונה נמחקה");
    } catch (e) {
      message.error(e?.response?.data?.message || "מחיקה נכשלה");
    }
  };

  const onMove = async (item, direction) => {
    const sorted = (items || []).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const idx = sorted.findIndex((x) => x.id === item.id);
    const target = direction === "up" ? idx - 1 : idx + 1;
    if (idx < 0 || target < 0 || target >= sorted.length) return;
    const copy = sorted.slice();
    [copy[idx], copy[target]] = [copy[target], copy[idx]];
    const ids = copy.map((x) => x.id);
    try {
      const reordered = await reorderAgentSiteSlider(ids);
      setItems(reordered);
    } catch (e) {
      message.error(e?.response?.data?.message || "שינוי סדר נכשלה");
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title="הוספה/עריכה של סלייד">
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Upload beforeUpload={onUpload} showUploadList={false} accept=".jpg,.jpeg,.png,.webp">
            <Button icon={<UploadOutlined />} loading={uploading}>
              העלאת תמונה (JPG/PNG/WEBP עד 5MB)
            </Button>
          </Upload>
          {form.image_url ? (
            <img
              src={form.image_url}
              alt="preview"
              style={{ width: "100%", maxHeight: 220, objectFit: "cover", borderRadius: 8 }}
            />
          ) : null}
          <Input placeholder="כותרת" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
          <TextArea
            rows={3}
            placeholder="תיאור קצר"
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
          />
          <Input
            placeholder="טקסט כפתור (אופציונלי)"
            value={form.button_text}
            onChange={(e) => setForm((p) => ({ ...p, button_text: e.target.value }))}
          />
          <Input
            placeholder="קישור כפתור (אופציונלי)"
            value={form.button_url}
            onChange={(e) => setForm((p) => ({ ...p, button_url: e.target.value }))}
          />
          <Input
            placeholder="קוד יעד אוטומטי (לדוגמה ATH)"
            value={form.destination_code}
            onChange={(e) =>
              setForm((p) => ({ ...p, destination_code: String(e.target.value || "").toUpperCase() }))
            }
          />
          <Input
            placeholder="שם יעד לתצוגה (לדוגמה אתונה)"
            value={form.destination_name}
            onChange={(e) => setForm((p) => ({ ...p, destination_name: e.target.value }))}
          />
          <Space>
            <Switch checked={form.is_active} onChange={(v) => setForm((p) => ({ ...p, is_active: v }))} />
            <Text>פעיל</Text>
          </Space>
          <Space>
            <Button type="primary" onClick={onSave} loading={saving}>
              {editingId ? "שמור שינויים" : "הוסף לסליידר"}
            </Button>
            {editingId ? <Button onClick={resetForm}>בטל עריכה</Button> : null}
          </Space>
        </Space>
      </Card>

      <Card title="תמונות קיימות" loading={loading}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {(items || [])
            .slice()
            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
            .map((item, idx, arr) => (
              <Card key={item.id} size="small">
                <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                  <img src={item.image_url} alt={item.title || "slider"} style={{ width: 120, height: 80, objectFit: "cover", borderRadius: 6 }} />
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <Title level={5} style={{ margin: 0 }}>{item.title || "ללא כותרת"}</Title>
                    <Text type="secondary">{item.description || "ללא תיאור"}</Text>
                    <div>
                      <Text type={item.is_active ? "success" : "warning"}>
                        {item.is_active ? "פעיל" : "לא פעיל"}
                      </Text>
                    </div>
                    {item.destination_code ? (
                      <div>
                        <Text type="secondary">
                          יעד: {item.destination_name || item.destination_code} ({item.destination_code})
                        </Text>
                      </div>
                    ) : null}
                  </div>
                  <Space>
                    <Button icon={<ArrowUpOutlined />} disabled={idx === 0} onClick={() => onMove(item, "up")} />
                    <Button icon={<ArrowDownOutlined />} disabled={idx === arr.length - 1} onClick={() => onMove(item, "down")} />
                    <Button icon={<EditOutlined />} onClick={() => onEdit(item)} />
                    <Button danger icon={<DeleteOutlined />} onClick={() => onDelete(item)} />
                  </Space>
                </div>
              </Card>
            ))}
          {items.length === 0 ? <Text type="secondary">עדיין לא הוגדרו תמונות סליידר.</Text> : null}
        </Space>
      </Card>

      <Card title="Preview בסיסי">
        {activeItems.length > 0 ? (
          <Carousel autoplay dots>
            {activeItems.map((item) => (
              <div key={item.id}>
                <div
                  style={{
                    minHeight: 240,
                    borderRadius: 10,
                    overflow: "hidden",
                    backgroundImage: `linear-gradient(105deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.25) 100%), url(${item.image_url})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    color: "#fff",
                    display: "flex",
                    alignItems: "end",
                    padding: 20,
                  }}
                >
                  <div>
                    <Title level={4} style={{ color: "#fff", marginBottom: 8 }}>{item.title || ""}</Title>
                    <Text style={{ color: "#fff" }}>{item.description || ""}</Text>
                    {item.button_text ? (
                      <div style={{ marginTop: 10 }}>
                        <Button type="primary">{item.button_text}</Button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </Carousel>
        ) : (
          <Text type="secondary">אין תמונות פעילות להצגה. יוצג fallback קיים באתר הציבורי.</Text>
        )}
      </Card>
    </Space>
  );
}
